import type { IMarket } from "~/types/event";
import type { Platform } from "~/types/platform";

const dedupeMarketsByPlatform = (
  markets: IMarket[],
  preferredMarketId: string
): IMarket[] => {
  const byPlatform = new Map<Platform, IMarket>();

  for (const market of markets) {
    const existing = byPlatform.get(market.platform);

    if (!existing) {
      byPlatform.set(market.platform, market);
      continue;
    }

    if (market.id === preferredMarketId) {
      byPlatform.set(market.platform, market);
      continue;
    }

    if (existing.id === preferredMarketId) {
      continue;
    }

    const existingLiquidity = existing.liquidity ?? 0;
    const nextLiquidity = market.liquidity ?? 0;

    if (nextLiquidity > existingLiquidity) {
      byPlatform.set(market.platform, market);
    }
  }

  return Array.from(byPlatform.values());
};

const collectGroupCandidates = (
  selectedMarket: IMarket,
  pool: IMarket[]
): IMarket[] => {
  const byId = new Map<string, IMarket>();

  const addMarket = (market: IMarket) => {
    byId.set(market.id, market);
  };

  addMarket(selectedMarket);

  if (selectedMarket.marketGroupKey) {
    pool
      .filter(
        (market) => market.marketGroupKey === selectedMarket.marketGroupKey
      )
      .forEach(addMarket);
  }

  if (selectedMarket.question) {
    pool
      .filter((market) => market.question === selectedMarket.question)
      .forEach(addMarket);
  }

  return Array.from(byId.values());
};

export const getCalculatorMarketGroup = (
  selectedMarket: IMarket,
  pool: IMarket[]
): { market: IMarket; groupedMarkets: IMarket[] } => {
  const groupCandidates = collectGroupCandidates(selectedMarket, pool);
  const dedupedMarkets = dedupeMarketsByPlatform(
    groupCandidates,
    selectedMarket.id
  );

  const market =
    dedupedMarkets.find((item) => item.id === selectedMarket.id) ??
    dedupedMarkets.find((item) => item.platform === selectedMarket.platform) ??
    selectedMarket;

  return {
    market,
    groupedMarkets: dedupedMarkets.filter((item) => item.id !== market.id),
  };
};
